import React, { useRef } from "react"
import styled from "@emotion/styled"
import { rgba } from "polished"
import { MdStar } from "react-icons/md"
import Slider from "react-slick"
import Section from "src/components/Section"
import Image from "src/components/Image"
import Grid from "src/components/Grid"
import Button from "src/components/Button"
import { colors, typography, util, globals, mq } from "src/styles"
import { Card } from "src/styles/globals"

const Wrapper = styled(Section)`
  .slick-track {
    display: flex !important;
    align-items: stretch;
  }
  .slick-slide {
    float: none !important;
    height: auto !important;
    > div {
      height: 100%;
    }
  }
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
  `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => hideMobile && "display: none"}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => hideDesktop && "display: none"}
  }
`

const ReviewsSlider = styled(Slider)``

export const ReviewMeta = styled.div`
  display: flex;
  align-items: flex-end;
  margin-top: 30px;
  flex-shrink: 0;
  justify-content: space-between;
  width: 100%;
  p {
    margin: 0;
  }
`

const ReviewText = styled.div`
  ${typography.articleTitleSmall}
  flex-shrink: 0;
`

const ReviewCardLarge = styled(Card)`
  height: 100%;
  align-items: stretch;
  display: flex;
  flex-direction: column;
  background: ${colors.white};
  width: 75vw;
  ${mq.mediumAndUp} {
    width: 50vw;
  }
  ${mq.largeAndUp} {
    width: 65vw;
    flex-direction: row;
  }
  ${mq.largerAndUp} {
    width: 50vw;
  }
  ${ReviewMeta} {
    p.title {
      font-weight: ${typography.bold};
      color: ${colors.textColor};
    }
    .subtitle {
      ${typography.bodySmall}
      color: ${colors.mainColor};
    }
  }
`

const ReviewCard = styled(Card)`
  background: ${colors.bgColor};
  // width: 40vw;
  width: 30vw;
  height: 100%;
  min-height: 18vw;
  padding: 16px 20px 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  background: ${colors.white};
  ${mq.mediumAndBelow} {
    width: 80vw;
  }
`

const Slide = styled.div`
  padding: 10px;
  height: 100%;
  ${mq.mediumAndBelow} {
    padding: 10px;
  }
  ${ReviewCard} {
    width: calc((100vw - 20px * 4 - (100vw / 7)) / 3);
    ${mq.largeAndBelow} {
      width: 40vw;
    }
    ${mq.mediumAndBelow} {
      width: 60vw;
    }
    ${mq.smallAndBelow} {
      width: 80vw;
    }
  }
`

const LargeCardImage = styled.div`
  width: 100%;
  // flex-grow: 1;
  ${mq.largeAndUp} {
    width: 30%;
  }
  .gatsby-image-wrapper {
    border-radius: var(--card-border-radius) var(--card-border-radius) 0 0;
    ${mq.largeAndUp} {
      height: 100%;
      border-radius: var(--card-border-radius) 0 0 var(--card-border-radius);
    }
  }
`

const LargeCardContent = styled.div`
  // height: 100%;
  width: 100%;
  min-height: 18vw;
  ${util.responsiveStyles("padding", 40, 30, 24, 20)}
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  flex-grow: 1;
  ${mq.largeAndUp} {
    width: 70%;
    height: 100%;
  }
  ${ReviewText} {
    margin-top: -0.2em;
  }
`

const RatingStars = styled.div`
  margin: 0.1em 0 0 -2px;
  display: flex;
  ${util.responsiveStyles("font-size", 22, 18, 18, 18)}
  svg {
    display: block;
    margin-right: 2px;
  }
`

const SectionHeader = styled.div`
  margin-top: -20px;
  ${globals.verticalSpacing("padding-bottom", 0.3)}
  ${({ type }) =>
    type !== "large"
      ? `
    display: flex;
    align-items: center;
    justify-content: space-between;
  `
      : `
    text-align: center;
  `}
`

const CenteredControls = styled.div`
  ${globals.verticalSpacing("padding-top", 0.5)}
  display: flex;
  justify-content: center;
`

const SliderControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  > button:first-of-type {
    margin-right: 10px;
  }
`

export const ThumbnailWrapper = styled.div`
  width: ${({ type }) => (type === "individual" ? "80px" : "120px")};
  flex-grow: 0;
`

export const ReviewImage = styled(Image)`
  border-radius: 4px;
  img {
    border-radius: 4px;
  }
`

export const renderRating = rating => {
  const stars = (count, active = true) =>
    [...Array(count)].map((e, i) => (
      <MdStar
        key={"ratingStar-" + i}
        color={active ? colors.yellow : rgba(colors.indigo, 0.2)}
      />
    ))

  if (!rating || rating === 0) {
    return false
  }

  return (
    <RatingStars>
      {stars(rating)}
      {stars(5 - rating, false)}
    </RatingStars>
  )
}

const Reviews = ({
  className,
  nextTheme,
  prevTheme,
  theme,
  spacing,
  isFirstSection,
  headingSize,
  headline,
  reviewsList,
  type,
  id,
  hideMobile,
  hideDesktop,
}) => {
  const slider = useRef(null)
  let settings = {
    arrows: false,
    slidesToShow: reviewsList.length * 2 <= 4 ? reviewsList.length * 2 : 4,
    infinite: true,
    variableWidth: true,
    centerMode: true,
    autoplay: true,
    swipe: true,
    speed: 500,
    // responsive: [
    //   {
    //     breakpoint: mq.largerBreakpoint,
    //     settings: {
    //       slidesToShow: reviewsList.length * 2 <= 3 ? reviewsList.length * 2 : 3,
    //     }
    //   },
    //   {
    //     breakpoint: mq.largeBreakpoint,
    //     settings: {
    //       slidesToShow: reviewsList.length * 2 <= 2 ? reviewsList.length * 2 : 2,
    //     }
    //   },
    //   {
    //     breakpoint: mq.mediumBreakpoint,
    //     settings: {
    //       slidesToShow: reviewsList.length * 2 <= 2 ? reviewsList.length * 2 : 2,
    //     }
    //   },
    //   {
    //     breakpoint: mq.smallBreakpoint,
    //     settings: {
    //       slidesToShow: 1,
    //     }
    //   }
    // ]
  }

  if (type === "large") {
    settings = {
      arrows: false,
      // slidesToShow: type === 'large' ? 1 : 2,
      slidesToShow: 1,
      slidesToScroll: 1,
      infinite: true,
      variableWidth: true,
      centerMode: true,
      swipe: true,
      speed: 500,
    }
  }

  const renderCard = (review, type) => {
    if (type === "large") {
      return (
        <ReviewCardLarge>
          <LargeCardImage type={type}>
            <Image
              image={review?.thumbnail?.asset?.gatsbyImageData}
              ratio={1}
              alt={review.title}
            />
          </LargeCardImage>
          <LargeCardContent>
            <ReviewText>{review.reviewText}</ReviewText>
            <ReviewMeta>
              <div>
                {review.title && <p className="title">{review.title}</p>}
                {review.subtitle && (
                  <p className="subtitle">{review.subtitle}</p>
                )}
                {renderRating(review.rating)}
              </div>
            </ReviewMeta>
          </LargeCardContent>
        </ReviewCardLarge>
      )
    }
    return (
      <ReviewCard>
        <ReviewText>{review.reviewText}</ReviewText>
        <ReviewMeta>
          <div>
            {review.title && (
              <p style={{ color: colors.textColor }}>{review.title}</p>
            )}
            {review.subtitle && (
              <p style={{ color: colors.lightTextColor }}>{review.subtitle}</p>
            )}
            {renderRating(review.rating)}
          </div>
          {review?.thumbnail?.asset?.gatsbyImageData && (
            <ThumbnailWrapper type={type}>
              <ReviewImage
                image={review?.thumbnail?.asset?.gatsbyImageData}
                ratio={type === "individual" ? 1 : false}
              />
            </ThumbnailWrapper>
          )}
        </ReviewMeta>
      </ReviewCard>
    )
  }

  return (
    <Wrapper
      className={className}
      prevTheme={prevTheme}
      setTheme={theme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      id={id}
      fullWidth
      vertical={spacing?.vertical}
      horizontal={spacing?.horizontal}
      hideMobile={hideMobile}
      hideDesktop={hideDesktop}
    >
      <Grid small="m [1] m" medium="1 [12] 1" large="m 1 [12] 1 m">
        <SectionHeader type={type}>
          {headline && (
            <h3 className={headingSize ? headingSize : "h3"}>
              {headline}
            </h3>
          )}
          {type !== "large" && (
            <SliderControls>
              <Button
                icon="arrow_back"
                onClick={() => slider?.current?.slickPrev()}
                shape="circle"
                title="Go to previous slide"
                setTheme="white"
              />
              <Button
                icon="arrow_forward"
                onClick={() => slider?.current?.slickNext()}
                shape="circle"
                title="Go to next slide"
                setTheme="white"
              />
            </SliderControls>
          )}
        </SectionHeader>
      </Grid>
      {reviewsList?.length > 0 && (
        <ReviewsSlider ref={slider} {...settings}>
          {reviewsList.map((review, index) => (
            <Slide key={review._key} firstSlide={index === 0}>
              {renderCard(review, type)}
            </Slide>
          ))}
          {reviewsList.map((review, index) => (
            <Slide
              key={review._key + "2"}
              lastSlide={index === reviewsList.length - 1}
            >
              {renderCard(review, type)}
            </Slide>
          ))}
        </ReviewsSlider>
      )}

      {type === "large" && (
        <CenteredControls>
          <SliderControls>
            <Button
              icon="arrow_back"
              onClick={() => slider?.current?.slickPrev()}
              shape="circle"
              title="Go to previous slide"
              setTheme="white"
            />
            <Button
              icon="arrow_forward"
              onClick={() => slider?.current?.slickNext()}
              shape="circle"
              title="Go to next slide"
              setTheme="white"
            />
          </SliderControls>
        </CenteredControls>
      )}
    </Wrapper>
  )
}

export default Reviews
